import axios from 'axios';
import { getAirportCode as fetchAirportCode } from './airportUtils';
import { csrfTokenManager } from '../utils/csrfTokenManager';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://travelapp-bens.onrender.com' 
  : 'http://localhost:5001';

export interface FlightResult {
  airline: string;
  flightTime: string;
  airport: string;
  destination: string;
  price?: number;
  duration: string;
  stops?: number;
  available: boolean;
  offer_id?: string;
  airlineLogo?: string;
  emissions?: string | number;
  stopoverCities?: string[];
  departureTime?: string;
  arrivalTime?: string;
  returnFlight?: FlightResult;
}

export interface FlightSearchParams {
  fromCity: string;
  toCity: string;
  departureDate: string;
  returnDate?: string;
  checkAvailabilityOnly?: boolean;
}

export interface FlightSearchResponse {
  outbound: FlightResult | null;
  inbound: FlightResult | null;
  allFlights: FlightResult[];
  flightOptions: {
    best: FlightResult[];
    cheapest: FlightResult[];
    fastest: FlightResult[];
  };
}

const fetchCSRFToken = async (): Promise<string> => {
  try {
    // Clear any cached token first to ensure we get a fresh one
    csrfTokenManager.clearToken();
    const token = await csrfTokenManager.getToken();
    console.log('🔐 FlightService: Successfully fetched CSRF token');
    return token;
  } catch (error) {
    console.error('🔐 FlightService: Error fetching CSRF token:', error);
    throw error;
  }
};

const getAirlineLogo = (airlineName: string): string => {
  try {
    // Duffel provides airline logos in their API response
    // We should use the airline logo URL from the offer.owner.logo_symbol_url
    // If that's not available, we can use a default airline logo
    if (!airlineName) {
      return '/airline-logos/default-airline.svg';
    }

    // For now, use a placeholder path until we get the actual logo URLs from Duffel
    return `/airline-logos/${airlineName.toLowerCase().replace(/\s+/g, '-')}.png`;

  } catch (error) {
    console.warn('Error getting airline logo:', error);
    return '/airline-logos/default-airline.svg';
  }
};

export const searchFlights = async (params: FlightSearchParams): Promise<FlightSearchResponse> => {
  try {
    
    // Extract IATA code if it's embedded in text (e.g., "**LHR**" or "LHR")
    const extractIATACode = (text: string): string => {
      const iataMatch = text.match(/\b([A-Z]{3})\b/);
      return iataMatch ? iataMatch[1] : text;
    };

    // Convert city names to airport codes using the airport service
    const fromCityCode = extractIATACode(params.fromCity);
    const toCityCode = extractIATACode(params.toCity);
    
    // If they're still not IATA codes (length !== 3 or not all uppercase), convert them
    const fromCity = (fromCityCode.length === 3 && fromCityCode === fromCityCode.toUpperCase()) 
      ? fromCityCode 
      : await fetchAirportCode(params.fromCity);
    
    const toCity = (toCityCode.length === 3 && toCityCode === toCityCode.toUpperCase()) 
      ? toCityCode 
      : await fetchAirportCode(params.toCity);
    
    const response = await axios.get(`${API_BASE_URL}/api/flights/search`, {
      params: {
        fromCity,
        toCity,
        departureDate: new Date(params.departureDate).toISOString(),
        returnDate: params.returnDate ? new Date(params.returnDate).toISOString() : undefined,
        cabinClass: 'economy',
        adults: 1,
        finalDestination: toCity
      }
    });


    if (!response.data || !response.data.flightOptions) {
      throw new Error('Invalid response format from server');
    }

    const { flightOptions } = response.data;

    // Verify the destination matches what we requested
    const verifyDestination = (flight: FlightResult) => {
      // More flexible destination matching
      const flightDest = flight.destination?.toUpperCase();
      const requestedDest = toCity?.toUpperCase();
      
      // Check exact match or if the requested destination is contained in the flight destination
      return flightDest === requestedDest || 
             flightDest?.includes(requestedDest) || 
             requestedDest?.includes(flightDest);
    };

    // Log for debugging
    console.log('Destination verification:', {
      requestedDestination: toCity,
      firstFlightDestination: flightOptions.best[0]?.destination,
      totalFlights: flightOptions.best.length
    });

    // Filter flights to ensure they reach the correct final destination
    const filteredOptions = {
      best: flightOptions.best.filter(verifyDestination),
      cheapest: flightOptions.cheapest.filter(verifyDestination),
      fastest: flightOptions.fastest.filter(verifyDestination)
    };

    // If no flights pass verification, use original results (they're already correct from API)
    const finalOptions = {
      best: filteredOptions.best.length > 0 ? filteredOptions.best : flightOptions.best,
      cheapest: filteredOptions.cheapest.length > 0 ? filteredOptions.cheapest : flightOptions.cheapest,
      fastest: filteredOptions.fastest.length > 0 ? filteredOptions.fastest : flightOptions.fastest
    };

    return {
      outbound: finalOptions.best[0] || null,
      inbound: finalOptions.best[0]?.returnFlight || null,
      allFlights: finalOptions.best || [],
      flightOptions: finalOptions
    };

  } catch (error) {
    console.error('Frontend: Flight search error:', error);
    throw error;
  }
};

export const searchTransportFlights = async (params: FlightSearchParams): Promise<FlightSearchResponse> => {
  try {
    
    // Get CSRF token from centralized manager
    const token = await fetchCSRFToken();
    
    // Convert city names to IATA codes
    let originCode: string;
    let destinationCode: string;
    
    try {
      originCode = await fetchAirportCode(params.fromCity);
      destinationCode = await fetchAirportCode(params.toCity);
    } catch (error) {
      throw new Error(`Unable to find airport codes for ${params.fromCity} or ${params.toCity}. Please use cities with commercial airports.`);
    }
    
    // Validate airport codes
    if (!originCode || originCode.length !== 3 || originCode === params.fromCity) {
      throw new Error(`${params.fromCity} does not appear to have a commercial airport. Please choose a different departure city.`);
    }
    
    if (!destinationCode || destinationCode.length !== 3 || destinationCode === params.toCity) {
      throw new Error(`${params.toCity} does not appear to have a commercial airport. Please choose a different destination city.`);
    }
    
    // Check if origin and destination are the same airport
    if (originCode === destinationCode) {
      throw new Error(`${params.fromCity} and ${params.toCity} use the same airport (${originCode}). Flight search between the same airport is not possible. Consider ground transportation instead.`);
    }
    
    // Format the request body WITH the data wrapper
    const requestBody = {
      data: {
        slices: [{
          origin: originCode,
          destination: destinationCode,
          departure_date: new Date(params.departureDate).toISOString().split('T')[0]
        }],
        passengers: [{ type: "adult" }],
        cabin_class: "economy"
      }
    };
    
    // Add return flight if needed
    if (params.returnDate) {
      requestBody.data.slices.push({
        origin: destinationCode,
        destination: originCode,
        departure_date: new Date(params.returnDate).toISOString().split('T')[0]
      });
    }
    
    
    // Include the CSRF token in the headers
    const response = await axios.post(`${API_BASE_URL}/api/transport/flights`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,  // Make sure this header name matches what your backend expects
      },
      withCredentials: true, // Important to include cookies if token is verified via cookies
    });

    if (!response.data || !response.data.flightOptions) {
      throw new Error('Invalid response format from server');
    }

    const { flightOptions } = response.data;

    return {
      outbound: flightOptions.best[0] || null,
      inbound: flightOptions.best[0]?.returnFlight || null,
      allFlights: flightOptions.best || [],
      flightOptions: flightOptions
    };

  } catch (error) {
    
    if (axios.isAxiosError(error)) {
      // Provide more specific error messages
      if (error.response?.status === 403) {
        console.error('🔐 CSRF Error in flight search:', error.response?.data);
        throw new Error('Authentication failed. Please refresh the page and try again.');
      } else if (error.response?.status === 500) {
        throw new Error('Flight search failed. This may be due to invalid airport codes or no flights available for this route.');
      } else if (error.response?.status === 422) {
        throw new Error('Invalid airport codes. Please ensure both cities have commercial airports.');
      }
    }
    
    console.error('🔐 FlightService: Full error details:', error);
    throw error;
  }
};

export const createBasicFlight = (data: {
  airline: string;
  flightTime: string;
  airport: string;
  destination: string;
}): FlightResult => {
  return {
    ...data,
    price: 0,
    duration: 'PT0H0M',
    stops: 0,
    available: true,
    offer_id: 'manual',
    airlineLogo: getAirlineLogo(data.airline),
    emissions: '0',
    departureTime: '',
    arrivalTime: '',
    stopoverCities: []
  };
};

// Add new function to fetch prices for surrounding dates
export async function fetchDateRangePrices(
  fromCity: string,
  toCity: string,
  baseDate: string,
  daysBeforeAfter: number = 3
): Promise<{date: string, price: number}[]> {
  try {
    // Create dates array for days before and after base date
    const dates: string[] = [];
    const baseDateObj = new Date(baseDate);
    
    // Add dates before the selected date
    for (let i = daysBeforeAfter; i > 0; i--) {
      const date = new Date(baseDateObj);
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    // Add the selected date
    dates.push(baseDate);
    
    // Add dates after the selected date
    for (let i = 1; i <= daysBeforeAfter; i++) {
      const date = new Date(baseDateObj);
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    // Make parallel requests for each date
    const pricePromises = dates.map(async (date) => {
      try {
        const response = await searchTransportFlights({
          fromCity,
          toCity,
          departureDate: date,
          checkAvailabilityOnly: true
        });
        
        // Find cheapest option for the date
        let cheapestPrice = Number.MAX_VALUE;
        
        if (response?.flightOptions?.cheapest && response.flightOptions.cheapest.length > 0) {
          response.flightOptions.cheapest.forEach(flight => {
            if (flight.price && flight.price < cheapestPrice) {
              cheapestPrice = flight.price;
            }
          });
        }
        
        return {
          date,
          price: cheapestPrice !== Number.MAX_VALUE ? cheapestPrice : 999999 // Default high price if none found
        };
      } catch (error) {
        console.error(`Error fetching price for date ${date}:`, error);
        return {
          date,
          price: 999999 // Default high price for error cases
        };
      }
    });
    
    const results = await Promise.all(pricePromises);
    return results.filter(result => result.price !== 999999); // Filter out error cases
  } catch (error) {
    console.error('Error fetching date range prices:', error);
    throw error;
  }
}
